<template>
  <div class="week-view h-full">
    <div class="week-header grid grid-cols-7 border-b">
      <div
        v-for="(day, index) in weekDays"
        :key="index"
        class="day-header-cell p-2 text-center font-medium"
        :class="{
          'bg-red-50': isHoliday(day.date),
          'ring-2 ring-primary ring-inset': isSameDay(
            day.date,
            props.selectedDate,
          ),
        }"
      >
        <div>{{ format(day.date, "EEE") }}</div>
        <div
          class="text-sm rounded-full w-7 h-7 flex items-center justify-center mx-auto"
          :class="{
            'bg-primary text-white': isToday(day.date),
            'cursor-pointer hover:bg-gray-100': !isToday(day.date),
          }"
          @click="$emit('date-click', day.date)"
          @dblclick="$emit('dblclick-date', day.date)"
        >
          {{ format(day.date, "d") }}
        </div>
        <div class="text-xs text-gray-500">{{ format(day.date, "MMM") }}</div>
      </div>
    </div>

    <div class="week-body grid grid-cols-7 h-full overflow-y-auto">
      <div
        v-for="(day, dayIndex) in weekDays"
        :key="dayIndex"
        class="day-column relative border-l border-gray-200"
        :data-date="day.date.toISOString()"
      >
        <div
          v-for="hour in hours"
          :key="hour.value"
          class="hour-cell border-b border-gray-100 flex flex-col min-h-[60px]"
          @click="handleTimeSlotClick(day.date, hour.value)"
          @dblclick="$emit('dblclick-date', setHours(day.date, hour.value))"
        >
          <div
            v-for="event in getEventsForHour(day.date, hour.value)"
            :key="event.id"
            class="rounded px-2 py-1 text-xs overflow-hidden cursor-move group relative mb-1"
            :class="dashboardStore.getEventColorClass(event)"
            @click.stop="$emit('event-click', event)"
            draggable="true"
            @dragstart="handleDragStart($event, event)"
            @dragend="handleDragEnd($event)"
          >
            <!-- Use slot if provided, otherwise use default rendering -->
            <slot
              name="event-tile"
              :event="event"
              :date="day.date"
              :view="'week'"
              :deletable="deletable"
              :delete-event="(id) => $emit('event-delete', id)"
            >
              <!-- Delete button -->
              <button
                v-if="deletable"
                class="absolute top-0 right-0 bg-white rounded-full p-0.5 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
                @click.stop="$emit('event-delete', event.id)"
              >
                <XMarkIcon class="h-3 w-3 text-red-500" />
              </button>

              <!-- Default event content -->
              <div class="flex flex-col">
                <div
                  v-if="eventTemplate.showTitle"
                  class="font-medium truncate"
                  :class="{
                    'line-clamp-1': eventTemplate.titleLines === 1,
                    'line-clamp-2': eventTemplate.titleLines === 2,
                  }"
                >
                  {{ event.title }}
                </div>

                <div
                  v-if="eventTemplate.showTime && event.time"
                  class="text-xs opacity-75"
                >
                  {{ event.time }}
                </div>

                <div
                  v-if="eventTemplate.showType && event.type"
                  class="text-xs opacity-75"
                >
                  {{ event.type }}
                </div>

                <div
                  v-if="eventTemplate.showStatus && event.status"
                  class="text-xs opacity-75"
                >
                  {{ event.status }}
                </div>

                <div
                  v-if="eventTemplate.showNotes && event.notes"
                  class="text-xs opacity-75 mt-1"
                  :class="{
                    'line-clamp-1': eventTemplate.notesLines === 1,
                    'line-clamp-2': eventTemplate.notesLines === 2,
                  }"
                >
                  {{ event.notes }}
                </div>
              </div>
            </slot>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useDashboardStore } from "@/store/dashboard";
import { XMarkIcon } from "@heroicons/vue/24/outline";
import { showToast } from "@/composables/swal";
import {
  addDays,
  format,
  getHours,
  getMinutes,
  isSameDay,
  parseISO,
  setHours,
  setMinutes,
  startOfWeek,
} from "date-fns";
import { computed, ref } from "vue";
import { OrderStatus } from "@/composables";
const dashboardStore = useDashboardStore();

interface Event {
  id: string;
  title: string;
  start: string;
  end?: string;
  time?: string;
  status: OrderStatus;
  type?: string;
  [key: string]: any;
}

interface Holiday {
  id: string;
  name: string;
  date: string;
  type: string;
}

// Define props
const props = defineProps({
  date: {
    type: Date,
    required: true,
  },
  selectedDate: {
    type: Date,
    required: true,
  },
  events: {
    type: Array as () => Event[],
    default: () => [],
  },
  holidays: {
    type: Array as () => Holiday[],
    default: () => [],
  },
  editable: {
    type: Boolean,
    default: true,
  },
  deletable: {
    type: Boolean,
    default: true,
  },
  eventTemplate: {
    type: Object,
    default: () => ({
      showTitle: true,
      showTime: true,
      showType: false,
      showStatus: false,
      showNotes: false,
      titleLines: 1,
      notesLines: 0,
    }),
  },
});

// Define emits
const emit = defineEmits([
  "date-click",
  "event-click",
  "time-slot-click",
  "event-drop",
  "event-delete",
  "dblclick-date",
]);

const draggedEvent = ref<Event | null>(null);

const weekDays = computed(() => {
  const days = [];
  const weekStart = startOfWeek(props.date);

  for (let i = 0; i < 7; i++) {
    days.push({
      date: addDays(weekStart, i),
    });
  }

  return days;
});

const hours = computed(() => {
  const result = [];
  for (let i = 0; i < 24; i++) {
    result.push({
      value: i,
      label:
        i === 0
          ? "12 AM"
          : i < 12
            ? `${i} AM`
            : i === 12
              ? "12 PM"
              : `${i - 12} PM`,
    });
  }
  return result;
});

const isToday = (date: Date) => {
  return isSameDay(date, new Date());
};

const isHoliday = (date: Date) => {
  return props.holidays.some((holiday) => {
    const holidayDate = parseISO(holiday.date);
    return isSameDay(holidayDate, date);
  });
};

const getEventsForDay = (date: Date) => {
  return props.events
    .filter((event) => {
      const eventDate = parseISO(event.start);
      return isSameDay(eventDate, date);
    })
    .map(
      (event) =>
        ({
          ...(event as Event),
          status: (event.status as OrderStatus) || OrderStatus.Pending,
        }) as Event,
    );
};

const getEventsForHour = (date: Date, hour: number) => {
  return props.events.filter((event) => {
    const eventDate = parseISO(event.start);
    return isSameDay(eventDate, date) && getHours(eventDate) === hour;
  });
};

const getEventPositionStyle = (event: Event) => {
  let hours = 0;
  let minutes = 0;

  if (event.time) {
    const [h, m] = event.time.split(":").map(Number);
    hours = h;
    minutes = m;
  } else if (event.start.includes("T")) {
    const date = parseISO(event.start);
    hours = getHours(date);
    minutes = getMinutes(date);
  }

  const top = hours * 80 + (minutes / 60) * 80; // 80px per hour

  return {
    top: `${top}px`,
    left: "2px",
    right: "2px",
    height: "60px",
  };
};

const handleTimeSlotClick = (date: Date, hour: number) => {
  if (!props.editable) return;

  const newDate = new Date(date);
  newDate.setHours(hour);
  newDate.setMinutes(0);
  emit("time-slot-click", newDate);
};

const handleDragStart = (e: DragEvent, event: Event) => {
  if (!props.editable || !e.dataTransfer) return;

  // Check if the event is on a holiday date
  const eventDate = parseISO(event.start);
  if (isHoliday(eventDate)) {
    e.preventDefault();
    showToast("Cannot move events from holiday dates", "warning");
    return;
  }

  e.dataTransfer.effectAllowed = "move";
  e.dataTransfer.setData("text/plain", event.id);
  draggedEvent.value = event;
};

const handleDragEnd = (e: DragEvent) => {
  if (!props.editable || !draggedEvent.value || !e.dataTransfer) return;

  // Find the hour cell under the drop position
  const hourElement = findClosestHourElement(e.clientY);
  // Find the day column under the drop position
  const dayColumn = findClosestDayColumn(e.clientX);

  if (hourElement && dayColumn) {
    const hourValue = parseInt(hourElement.dataset.hour || "0", 10);
    const rect = hourElement.getBoundingClientRect();
    const relativeY = e.clientY - rect.top;
    const minutes = Math.floor((relativeY / rect.height) * 60);

    // Get the correct day from the day column
    const dayDateStr = dayColumn.getAttribute("data-date");
    const newDate = dayDateStr ? new Date(dayDateStr) : new Date();

    newDate.setHours(hourValue);
    newDate.setMinutes(minutes);

    emit("event-drop", draggedEvent.value.id, newDate);
  }

  draggedEvent.value = null;
};

const findClosestHourElement = (clientY: number) => {
  const hourElements = document.querySelectorAll(".hour-cell");
  let closestElement = null;
  let closestDistance = Infinity;

  hourElements.forEach((element, index) => {
    const rect = element.getBoundingClientRect();
    const distance = Math.abs(rect.top + rect.height / 2 - clientY);

    if (distance < closestDistance) {
      closestDistance = distance;
      closestElement = element;
      (closestElement as HTMLElement).dataset.hour = (index % 24).toString();
    }
  });

  return closestElement as HTMLElement | null;
};

const findClosestDayColumn = (clientX: number) => {
  const dayColumns = document.querySelectorAll(".day-column");
  let closestElement = null;
  let closestDistance = Infinity;

  dayColumns.forEach((element) => {
    const rect = element.getBoundingClientRect();
    const distance = Math.abs(rect.left + rect.width / 2 - clientX);
    if (distance < closestDistance) {
      closestDistance = distance;
      closestElement = element;
    }
  });

  return closestElement as HTMLElement | null;
};
</script>

<style scoped>
.week-view {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.week-body {
  flex: 1;
  overflow-y: auto;
}

.hour-cell {
  position: relative;
  display: flex;
  flex-direction: column;
}
</style>
